import React from 'react';
import { Entity, PolygonGraphics, PolylineGraphics } from "resium";
import { Cartesian3, Color, PolygonHierarchy, HeightReference, PolylineDashMaterialProperty } from "cesium";



const AssetFootprintLayer = ({ assets }) => {
  // Skip if no assets
  if (!assets || assets.length === 0) {
    return null;
  }



  // Helper function to create polygon hierarchy from GeoJSON geometry
  const createPolygonHierarchy = (asset) => {
    // Check if asset has GeoJSON geometry data
    if (!asset.geojson) {
      console.warn(`Asset ${asset.id} does not have GeoJSON geometry data.`);
      return null;
    }

    try {
      // Parse the GeoJSON geometry
      const geojson = JSON.parse(asset.geojson);
      
      // Check the geometry type
      if (!geojson || !geojson.type) {
        console.warn(`Asset ${asset.id} has invalid GeoJSON format.`);
        return null;
      }

      // Handle different geometry types - using ground level coordinates only
      switch (geojson.type) {
        case 'Polygon':
          // Use the first (outer) ring of coordinates
          if (geojson.coordinates && geojson.coordinates.length > 0) {
            const positions = geojson.coordinates[0].map(coord => 
              Cartesian3.fromDegrees(coord[0], coord[1]) // No height specified
            );
            return new PolygonHierarchy(positions);
          }
          break;
          
        case 'MultiPolygon':
          // Use the first polygon's first (outer) ring
          if (geojson.coordinates && geojson.coordinates.length > 0 && 
              geojson.coordinates[0].length > 0) {
            const positions = geojson.coordinates[0][0].map(coord => 
              Cartesian3.fromDegrees(coord[0], coord[1]) // No height specified
            );
            return new PolygonHierarchy(positions);
          }
          break;
          
        case 'Point':
          // For point geometries, create a small square around the point (fallback)
          if (geojson.coordinates && geojson.coordinates.length >= 2) {
            const [longitude, latitude] = geojson.coordinates;
            const size = 0.0005; // Small size for visibility 
            const positions = [
              Cartesian3.fromDegrees(longitude - size, latitude - size),
              Cartesian3.fromDegrees(longitude + size, latitude - size),
              Cartesian3.fromDegrees(longitude + size, latitude + size),
              Cartesian3.fromDegrees(longitude - size, latitude + size),
            ];
            return new PolygonHierarchy(positions);
          }
          break;
          
        case 'LineString':
          // For lines, create a thin buffer around the line (approximate)
          if (geojson.coordinates && geojson.coordinates.length >= 2) {
            // This is a very simplified approach - in production you would use a proper buffering algorithm
            const buffer = 0.0001; // Small buffer for visibility
            const positions = [];
            
            // Create rough buffer by offsetting line coordinates 
            for (let i = 0; i < geojson.coordinates.length; i++) {
              const [lon, lat] = geojson.coordinates[i];
              // Add points in clockwise order around the original point
              positions.push(Cartesian3.fromDegrees(lon - buffer, lat - buffer));
              positions.push(Cartesian3.fromDegrees(lon + buffer, lat - buffer));
              positions.push(Cartesian3.fromDegrees(lon + buffer, lat + buffer));
              positions.push(Cartesian3.fromDegrees(lon - buffer, lat + buffer));
            }
            
            return new PolygonHierarchy(positions);
          }
          break;
          
        default:
          console.warn(`Asset ${asset.id} has unsupported geometry type: ${geojson.type}`);
          return null;
      }
    } catch (error) {
      console.error(`Error parsing GeoJSON for asset ${asset.id}:`, error);
      return null;
    }
    
    return null;
  };

  return (
    <>
      {assets.map(asset => {
        // Skip assets without valid coordinates
        if (typeof asset.center_lat !== 'number' || typeof asset.center_lng !== 'number') {
          return null;
        }
        
        // Log if geojson is missing for debugging (remove in production)
        if (!asset.geojson) {
          console.warn(`Asset ${asset.id} (${asset.name || 'unnamed'}) is missing geometry data.`);
          return null;
        }
        
        // Get polygon hierarchy for the asset using its actual geometry
        const polygonHierarchy = createPolygonHierarchy(asset);
        if (!polygonHierarchy) {
          return null;
        }
        
        // Create polyline positions for dashed outline by closing the polygon
        const outlinePositions = [...polygonHierarchy.positions, polygonHierarchy.positions[0]];
        
        return (
          <Entity
            key={`footprint-${asset.id}`}
            id={`footprint-outline-${asset.id}`}
            name={`${asset.name || `Asset ${asset.id}`} Footprint Outline`}
            properties={{ 
              assetData: asset,
              isFootprint: true,
              assetType: asset.subtype_name || 'Unknown',
              assetCategory: asset.category_name || 'Unknown',
              assetNetwork: asset.network_name || 'Unknown'
            }}
          >
            <PolylineGraphics
              positions={outlinePositions}
              width={3}
              material={new PolylineDashMaterialProperty({
                color: Color.RED,
                dashLength: 16.0,
                gapColor: Color.TRANSPARENT
              })}
              heightReference={HeightReference.CLAMP_TO_GROUND}
              clampToGround={true}
            />
          </Entity>
        );
      })}
    </>
  );
};

export default AssetFootprintLayer; 